import { Injectable, OnDestroy } from "@angular/core";
import { environment } from "environments/environment";
import { Observable, Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class WebsocketService implements OnDestroy {
  private ws: WebSocket | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  private subjects = new Map<string, Subject<any>>();
  private subscriptions = new Map<string, string>();

  private subIdCounter = 0;
  private manuallyDisconnected = false;

  connect(topic: string): Observable<any> {
    let subject = this.subjects.get(topic);

    if (!subject) {
      subject = new Subject<any>();
      this.subjects.set(topic, subject);
    }

    this.ensureConnection();

    if (this.isSocketOpen() && !this.subscriptions.has(topic)) {
      this.stompSubscribe(topic);
    }

    return subject.asObservable();
  }

  unsubscribe(topic: string): void {
    const subId = this.subscriptions.get(topic);

    if (this.isSocketOpen() && subId) {
      this.ws!.send(`UNSUBSCRIBE\nid:${subId}\n\n\0`);
    }

    this.subscriptions.delete(topic);

    const subject = this.subjects.get(topic);
    if (subject) {
      subject.complete();
      this.subjects.delete(topic);
    }

    console.log(`[WS] Unsubscribed from ${topic}`);

    if (this.subjects.size === 0) {
      this.disconnect();
    }
  }

  disconnect(): void {
    this.manuallyDisconnected = true;

    this.clearReconnectTimer();
    this.stopHeartbeat();

    if (this.isSocketOpen()) {
      this.ws!.send("DISCONNECT\n\n\0");
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.subjects.forEach((subject) => subject.complete());
    this.subjects.clear();
    this.subscriptions.clear();

    console.log("[WS] Disconnected");
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private ensureConnection(): void {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.createWebSocket();
  }

  private createWebSocket(): void {
    this.manuallyDisconnected = false;

    this.ws = new WebSocket(`${environment.wsUrl}/websocket`);

    this.ws.onopen = () => {
      console.log("[WS] WebSocket opened");

      this.ws!.send(
        "CONNECT\naccept-version:1.2\nheart-beat:10000,10000\n\n\0",
      );
    };

    this.ws.onmessage = (event: MessageEvent) => {
      const raw = String(event.data);

      if (raw === "\n") {
        return;
      }

      const frames = this.splitStompFrames(raw);

      for (const frame of frames) {
        this.handleFrame(frame);
      }
    };

    this.ws.onerror = (event) => {
      console.error("[WS] WebSocket error", event);
    };

    this.ws.onclose = (event) => {
      console.warn("[WS] Connection closed", event.code, event.reason);

      this.stopHeartbeat();
      this.subscriptions.clear();
      this.ws = null;

      if (!this.manuallyDisconnected && this.subjects.size > 0) {
        this.scheduleReconnect();
      }
    };
  }

  private handleFrame(frame: string): void {
    if (!frame.trim()) {
      return;
    }

    const commandEnd = frame.indexOf("\n");
    const command = commandEnd === -1 ? frame.trim() : frame.slice(0, commandEnd);

    if (command === "CONNECTED") {
      console.log("[WS] STOMP CONNECTED");

      const headers = this.parseHeaders(frame);
      const heartbeat = headers.get("heart-beat");

      const serverRecv = heartbeat
        ? Number(heartbeat.split(",")[1] || 0)
        : 0;

      const sendInterval = Math.max(10000, serverRecv);

      if (sendInterval > 0) {
        this.startHeartbeat(sendInterval);
      }

      this.subjects.forEach((_, topic) => {
        if (!this.subscriptions.has(topic)) {
          this.stompSubscribe(topic);
        }
      });

      return;
    }

    if (command === "MESSAGE") {
      this.handleMessage(frame);
      return;
    }

    if (command === "ERROR") {
      console.error("[WS] STOMP ERROR frame:", frame);
      return;
    }

    console.debug("[WS] Ignored STOMP frame:", frame);
  }

  private stompSubscribe(topic: string): void {
    if (!this.isSocketOpen()) {
      return;
    }

    if (this.subscriptions.has(topic)) {
      return;
    }

    const subId = `sub-${this.subIdCounter++}`;

    this.ws!.send(`SUBSCRIBE\nid:${subId}\ndestination:${topic}\n\n\0`);

    this.subscriptions.set(topic, subId);

    console.log(`[WS] Subscribed to ${topic} (${subId})`);
  }

  private handleMessage(frame: string): void {
    const headers = this.parseHeaders(frame);
    const destination = headers.get("destination");

    const body = this.extractBody(frame);

    try {
      const data = JSON.parse(body);

      if (destination && this.subjects.has(destination)) {
        this.subjects.get(destination)!.next(data);
        return;
      }

      this.subjects.forEach((subject) => subject.next(data));
    } catch (error) {
      console.error("[WS] Failed to parse message body", error, body);
    }
  }

  private parseHeaders(frame: string): Map<string, string> {
    const headers = new Map<string, string>();

    const headerEnd = frame.indexOf("\n\n");
    const headerPart = headerEnd === -1 ? frame : frame.slice(0, headerEnd);

    const lines = headerPart.split("\n");
    lines.shift();

    for (const line of lines) {
      const separatorIndex = line.indexOf(":");

      if (separatorIndex === -1) {
        continue;
      }

      const key = line.slice(0, separatorIndex).trim();
      const value = line.slice(separatorIndex + 1).trim();

      headers.set(key, value);
    }

    return headers;
  }

  private extractBody(frame: string): string {
    const bodyStart = frame.indexOf("\n\n");

    if (bodyStart === -1) {
      return "";
    }

    return frame.slice(bodyStart + 2).replace(/\0$/, "");
  }

  private splitStompFrames(raw: string): string[] {
    return raw
      .split("\0")
      .map((frame) => frame.replace(/\s+$/, ""))
      .filter(Boolean);
  }

  private startHeartbeat(intervalMs: number): void {
    this.stopHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (this.isSocketOpen()) {
        this.ws!.send("\n");
      }
    }, intervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;

      if (!this.manuallyDisconnected && this.subjects.size > 0) {
        console.log("[WS] Reconnecting...");
        this.createWebSocket();
      }
    }, 5000);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private isSocketOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}